import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Table,
  Alert,
  ButtonGroup,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchNoticias,
  createNoticia,
  updateNoticia,
  deleteNoticia,
} from "../../redux/noticiasSlice";
import YouTubeEmbed from "../../components/YouTubeEmbed" 
import { parseYouTube } from "../../utils/youtube"

const NoticiasAdminPage = () => {
  const dispatch = useDispatch();
  const { noticias, loading, error } = useSelector((state) => state.noticias);

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentNoticia, setCurrentNoticia] = useState({
    titulo: "",
    descripcion: "",
    fecha_publicacion: "",
    imagen: null,
    imagen_url: "",
    video_url: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    titulo: "",
    descripcion: "",
    fecha_publicacion: "",
    imagen: "",
    general: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchNoticias());
  }, [dispatch]);

  const handleCloseModal = () => {
    setValidationErrors({
      titulo: "",
      descripcion: "",
      fecha_publicacion: "",
      imagen: "",
      general: "",
    });
    setShowModal(false);
    setIsEditMode(false);
    setCurrentNoticia({
      titulo: "",
      descripcion: "",
      fecha_publicacion: "",
      imagen: null,
      imagen_url: "",
    });
  };

  const handleShowModal = (noticia = null) => {
    const today = new Date().toISOString().split("T")[0];

    if (noticia) {
      // Formatear la fecha para el input date (YYYY-MM-DD)
      const fechaNoticia = noticia.fecha_publicacion
        ? new Date(noticia.fecha_publicacion).toISOString().split("T")[0]
        : today;

      setCurrentNoticia({
        ...noticia,
        imagen: null,
        imagen_url: noticia.imagen_url,
        fecha_publicacion: fechaNoticia,
      });
      setIsEditMode(true);
    } else {
      setCurrentNoticia({
        titulo: "",
        descripcion: "",
        fecha_publicacion: today,
        imagen: null,
        imagen_url: "",
      });
      setIsEditMode(false);
    }

    setValidationErrors({
      titulo: "",
      descripcion: "",
      fecha_publicacion: "",
      imagen: "",
      general: "",
    });
    setShowModal(true);
  };
  //FORMATO FECHA PANEL ADMIN FORMAL:
  const formatFechaPublicacion = (fechaStr) => {
    if (!fechaStr) return "-";

    const soloFecha = fechaStr.split("T")[0];
    const date = new Date(`${soloFecha}T00:00:00Z`);
    return new Intl.DateTimeFormat("es-CL", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "titulo":
        if (!value.trim()) {
          error = "El título es obligatorio.";
        } else if (value.length > 255) {
          error = "El título no debe exceder los 255 caracteres.";
        }
        break;
      case "descripcion":
        if (!value.trim()) {
          error = "La descripción es obligatoria.";
        } else if (value.length < 10) {
          error = "La descripción debe tener al menos 10 caracteres.";
        }
        break;
      case "fecha_publicacion":
        if (!value) {
          error = "La fecha de publicación es obligatoria.";
        } else {
          // Validar formato de fecha simple (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(value)) {
            error = "Formato de fecha inválido (use YYYY-MM-DD)";
          } else if (new Date(value) > new Date()) {
            error = "La fecha de publicación no puede ser futura.";
          }
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "imagen" && files && files[0]) {
      const imagenUrl = URL.createObjectURL(files[0]);
      setCurrentNoticia({
        ...currentNoticia,
        imagen: files[0],
        imagen_url: imagenUrl,
      });
    } else {
      setCurrentNoticia({ ...currentNoticia, [name]: value });
    }

    const error = validateField(name, value);
    setValidationErrors({ ...validationErrors, [name]: error });
  };

  const isFormValid = () => {
    return (
      !validationErrors.titulo &&
      !validationErrors.descripcion &&
      !validationErrors.fecha_publicacion &&
      currentNoticia.titulo.trim() !== "" &&
      currentNoticia.descripcion.trim() !== "" &&
      currentNoticia.fecha_publicacion.trim() !== ""
    );
  };

  const handleSaveNoticia = async () => {
    setIsSaving(true);
    setValidationErrors((prev) => ({ ...prev, general: "" }));

    try {
      const formData = new FormData();
      formData.append("titulo", currentNoticia.titulo);
      formData.append("descripcion", currentNoticia.descripcion);
      formData.append("video_url", currentNoticia.video_url || "");

      // Asegurar que la fecha esté en formato YYYY-MM-DD
      const fechaFormateada = currentNoticia.fecha_publicacion.split("T")[0];
      formData.append("fecha_publicacion", fechaFormateada);

      if (isEditMode && !currentNoticia.imagen) {
        formData.append("imagen_url", currentNoticia.imagen_url);
      } else if (currentNoticia.imagen) {
        formData.append("imagen", currentNoticia.imagen);
      }

      if (isEditMode) {
        await dispatch(
          updateNoticia({ id: currentNoticia.id, noticia: formData }),
        ).unwrap();
        Swal.fire({
          icon: "success",
          title: "Noticia actualizada",
          text: "La noticia se ha actualizado correctamente.",
        });
      } else {
        await dispatch(createNoticia(formData)).unwrap();
        Swal.fire({
          icon: "success",
          title: "Noticia creada",
          text: "La noticia se ha creado correctamente.",
        });
      }

      dispatch(fetchNoticias());
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar la noticia:", error);

      let errorMessage =
        "Hubo un error al guardar la noticia. Por favor, inténtalo nuevamente.";

      if (error.payload) {
        if (Array.isArray(error.payload.errors)) {
          const backendErrors = {};
          error.payload.errors.forEach((err) => {
            backendErrors[err.field || err.path] = err.msg || err.message;
          });
          setValidationErrors({ ...validationErrors, ...backendErrors });
          errorMessage = "Por favor corrige los errores en el formulario.";
        } else if (error.payload.error) {
          setValidationErrors((prev) => ({
            ...prev,
            general: error.payload.error,
          }));
          errorMessage = error.payload.error;
        }
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNoticia = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deleteNoticia(id)).unwrap();
        dispatch(fetchNoticias());
        Swal.fire({
          icon: "success",
          title: "Noticia eliminada",
          text: "La noticia se ha eliminado correctamente.",
        });
      } catch (error) {
        console.error("Error al eliminar la noticia:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.payload?.error || "Hubo un error al eliminar la noticia.",
        });
      }
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Gestión de Noticias</h1>
        <Button
          onClick={() => handleShowModal()}
          variant="success"
          className="d-flex align-items-center"
        >
          <i className="bi bi-plus-circle me-2"></i>
          <span className="d-none d-sm-inline">Crear Noticia</span>
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error.message ||
            (typeof error === "string" ? error : "Ocurrió un error")}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="d-block d-md-none alert alert-info mb-3">
            <i className="bi bi-phone me-1"></i>
            Desliza horizontalmente para ver todas las columnas
          </div>

          <div className="table-responsive">
            <Table striped bordered hover className="small">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Título</th>
                  <th className="d-none d-md-table-cell">Descripción</th>
                  <th className="text-nowrap">Publicación</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {noticias.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      No hay noticias registradas
                    </td>
                  </tr>
                ) : (
                  noticias.map((noticia) => (
                    <tr key={noticia.id}>
                      <td>{noticia.id}</td>
                      <td
                        title={noticia.titulo}
                        className="text-truncate"
                        style={{ maxWidth: "150px" }}
                      >
                        {noticia.titulo}
                      </td>
                      <td
                        title={noticia.descripcion}
                        className="d-none d-md-table-cell text-truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        {noticia.descripcion}
                      </td>
                      <td className="text-nowrap">
                        {formatFechaPublicacion(noticia.fecha_publicacion)}
                      </td>
                      <td className="text-center">
                        <ButtonGroup size="sm">
                          <Button
                            variant="outline-primary"
                            onClick={() => handleShowModal(noticia)}
                            title="Editar"
                          >
                            <i className="bi bi-pencil"></i>
                            <span className="d-none d-lg-inline ms-1">
                              Editar
                            </span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => handleDeleteNoticia(noticia.id)}
                            title="Eliminar"
                          >
                            <i className="bi bi-trash"></i>
                            <span className="d-none d-lg-inline ms-1">
                              Eliminar
                            </span>
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i
              className={`bi ${
                isEditMode ? "bi-pencil-square" : "bi-plus-circle"
              } me-2`}
            ></i>
            {isEditMode ? "Editar Noticia" : "Crear Noticia"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationErrors.general && (
            <Alert
              variant="danger"
              dismissible
              onClose={() =>
                setValidationErrors((prev) => ({ ...prev, general: "" }))
              }
            >
              {validationErrors.general}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3" controlId="titulo">
              <Form.Label>Título *</Form.Label>
              <Form.Control
                type="text"
                name="titulo"
                value={currentNoticia.titulo}
                onChange={handleChange}
                isInvalid={!!validationErrors.titulo}
                placeholder="Título de la noticia"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.titulo}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="descripcion">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="descripcion"
                value={currentNoticia.descripcion}
                onChange={handleChange}
                isInvalid={!!validationErrors.descripcion}
                placeholder="Descripción detallada de la noticia"
              />

              {/* Instrucciones de uso para el admin */}
              <Form.Text muted>
                Puedes agregar:
                <br />• Enlaces: por ejemplo <code>
                  https://sinterec.cl
                </code>{" "}
                (se mostrarán como links clicables).
                <br />• Imágenes por URL: enlaces que terminen en{" "}
                <code>.jpg</code>, <code>.png</code>, <code>.gif</code>,{" "}
                <code>.webp</code>, etc. se incrustarán en la noticia.
                <br />• Hashtags: por ejemplo <code>#beneficios</code>,{" "}
                <code>#dataCenter</code>, <code>#SINTEREC</code>. Se verán como
                etiquetas destacadas.
                <br />
                No es necesario ni recomendable usar etiquetas HTML, solo texto
                plano.
              </Form.Text>

              <Form.Control.Feedback type="invalid">
                {validationErrors.descripcion}
              </Form.Control.Feedback>
              <Form.Group className="mb-3">
                <Form.Label>Video de YouTube (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  name="video_url"
                  value={currentNoticia.video_url}
                  onChange={handleChange}
                  placeholder="https://youtu.be/... o https://youtube.com/shorts/..."
                />
                <Form.Text className="text-muted">
                  Pega el enlace de un video o Short de YouTube. Se mostrará
                  reproducible en la noticia.
                </Form.Text>
              </Form.Group>
            </Form.Group>

            <Form.Group className="mb-3" controlId="fecha_publicacion">
              <Form.Label>Fecha de Publicación *</Form.Label>
              <Form.Control
                type="date"
                name="fecha_publicacion"
                value={currentNoticia.fecha_publicacion}
                onChange={handleChange}
                isInvalid={!!validationErrors.fecha_publicacion}
              />
              <Form.Text className="text-muted">
                Máxima fecha permitida: {new Date().toLocaleDateString("es-CL")}
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {validationErrors.fecha_publicacion}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="imagen">
              <Form.Label>Imagen</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                name="imagen"
                onChange={handleChange}
                className="mb-2"
              />
              {currentNoticia.imagen_url && (
                <div className="mt-2 text-center">
                  <img
                    src={currentNoticia.imagen_url}
                    alt="Vista previa"
                    className="img-thumbnail"
                    style={{ maxHeight: "200px" }}
                  />
                  <p className="text-muted small mt-2">
                    {isEditMode
                      ? "Imagen actual. Sube una nueva para reemplazarla."
                      : "Vista previa de la imagen"}
                  </p>
                </div>
              )}
            </Form.Group>
          </Form>
          {currentNoticia.video_url &&
            parseYouTube(currentNoticia.video_url) && (
              <div className="mb-3">
                <small className="text-success">✓ Video detectado</small>
                <YouTubeEmbed
                  url={currentNoticia.video_url}
                  titulo="Previsualización"
                />
              </div>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button
            variant={isEditMode ? "warning" : "success"}
            onClick={handleSaveNoticia}
            disabled={!isFormValid() || isSaving}
          >
            {isSaving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                {isEditMode ? "Actualizando..." : "Creando..."}
              </>
            ) : (
              <>
                <i
                  className={`bi ${
                    isEditMode ? "bi-check-circle" : "bi-save"
                  } me-1`}
                ></i>
                {isEditMode ? "Actualizar" : "Guardar"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NoticiasAdminPage;
